import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { ApiAlertError } from '@decorators/api-alert-error';
import { GIFileType } from '@enums/gi-file-type.enum';
import { GI_API_ROUTE_BASE } from '@gi/api/routes';
import { ContractAnnexLineCharge } from '@gi/enums/contract-annex-line-charge.enum';
import { ContractMailTemplateType } from '@gi/enums/contract-mail-template-type.enum';
import { ProspectAcquisitionChannel } from '@gi/enums/prospect-acquisition-channel.enum';
import { TiimeBusinessProAccountIntention } from '@gi/enums/tiime-business-pro-account-intention.enum';
import { shareReplay1 } from '@helpers/rxjs.helper';
import { MailTemplateField } from '@interfaces/mail-template-field';
import { CodeAndLabel } from '@models/code-and-label';

/**
 * GI Cached Data Api Service.
 */
@Injectable({
  providedIn: 'root'
})
export class GICachedDataApiService {
  private prospectAcquisitionChannelsCache: Observable<CodeAndLabel<ProspectAcquisitionChannel>[]>;
  private tiimeBusinessProAccountIntentionsCache: Observable<CodeAndLabel<TiimeBusinessProAccountIntention>[]>;
  private contractAnnexLineTypesCache: Observable<CodeAndLabel<string>[]>;
  private contractAnnexLineChargesCache: Observable<CodeAndLabel<ContractAnnexLineCharge>[]>;
  private contractEmailTemplateFieldsCache: Observable<Record<string, MailTemplateField[]>>;
  private emailTemplateTypesCache: Observable<CodeAndLabel<ContractMailTemplateType>[]>;
  private contractLossReasonsCache: Observable<CodeAndLabel<string>[]>;
  private filesTypesCache: Observable<CodeAndLabel<GIFileType>[]>;

  constructor(private readonly http: HttpClient) {}

  @ApiAlertError()
  getProspectAcquisitionChannels(): Observable<CodeAndLabel<ProspectAcquisitionChannel>[]> {
    if (!this.prospectAcquisitionChannelsCache) {
      const url = `${GI_API_ROUTE_BASE}/prospect_acquisition_channels`;

      this.prospectAcquisitionChannelsCache = this.http
        .get<CodeAndLabel<ProspectAcquisitionChannel>[]>(url)
        .pipe(shareReplay1());
    }

    return this.prospectAcquisitionChannelsCache;
  }

  @ApiAlertError()
  getTiimeBusinessProAccountIntentions(): Observable<CodeAndLabel<TiimeBusinessProAccountIntention>[]> {
    if (!this.tiimeBusinessProAccountIntentionsCache) {
      const url = `${GI_API_ROUTE_BASE}/tiime_business_pro_account_intentions`;

      this.tiimeBusinessProAccountIntentionsCache = this.http
        .get<CodeAndLabel<TiimeBusinessProAccountIntention>[]>(url)
        .pipe(shareReplay1());
    }

    return this.tiimeBusinessProAccountIntentionsCache;
  }

  @ApiAlertError()
  getContractAnnexLineTypes(): Observable<CodeAndLabel<string>[]> {
    if (!this.contractAnnexLineTypesCache) {
      const url = `${GI_API_ROUTE_BASE}/contract_annex_line_types`;

      this.contractAnnexLineTypesCache = this.http.get<CodeAndLabel<string>[]>(url).pipe(shareReplay1());
    }

    return this.contractAnnexLineTypesCache;
  }

  @ApiAlertError()
  getContractAnnexLineCharges(): Observable<CodeAndLabel<ContractAnnexLineCharge>[]> {
    if (!this.contractAnnexLineChargesCache) {
      const url = `${GI_API_ROUTE_BASE}/contract_annex_line_charge_choices`;

      this.contractAnnexLineChargesCache = this.http
        .get<CodeAndLabel<ContractAnnexLineCharge>[]>(url)
        .pipe(shareReplay1());
    }

    return this.contractAnnexLineChargesCache;
  }

  @ApiAlertError()
  getContractEmailTemplateFields(): Observable<Record<string, MailTemplateField[]>> {
    if (!this.contractEmailTemplateFieldsCache) {
      const url: string = `${GI_API_ROUTE_BASE}/contract_email_template_fields`;

      this.contractEmailTemplateFieldsCache = this.http
        .get<Record<string, MailTemplateField[]>>(url)
        .pipe(shareReplay1());
    }

    return this.contractEmailTemplateFieldsCache;
  }

  @ApiAlertError()
  getEmailTemplateTypes(): Observable<CodeAndLabel<ContractMailTemplateType>[]> {
    if (!this.emailTemplateTypesCache) {
      const url = `${GI_API_ROUTE_BASE}/email_template_types`;

      this.emailTemplateTypesCache = this.http.get<CodeAndLabel<ContractMailTemplateType>[]>(url).pipe(shareReplay1());
    }

    return this.emailTemplateTypesCache;
  }

  @ApiAlertError()
  getContractLossReasons(): Observable<CodeAndLabel<string>[]> {
    if (!this.contractLossReasonsCache) {
      const url = `${GI_API_ROUTE_BASE}/contract_loss_reasons`;

      this.contractLossReasonsCache = this.http.get<CodeAndLabel<string>[]>(url).pipe(shareReplay1());
    }

    return this.contractLossReasonsCache;
  }

  @ApiAlertError()
  getFileTypes(): Observable<CodeAndLabel<GIFileType>[]> {
    if (!this.filesTypesCache) {
      const url = `${GI_API_ROUTE_BASE}/file_types`;

      this.filesTypesCache = this.http.get<CodeAndLabel<GIFileType>[]>(url).pipe(shareReplay1());
    }

    return this.filesTypesCache;
  }
}
